import {isFeatureEnabled} from 'feature-flags';
import {
  Checkbox,
  CheckboxProps,
  Choice,
  ChoiceProps,
  DataStatus,
  IconButton,
  Separator,
  Switch,
} from 'platform/components';
import {
  Box,
  getSize,
  Hide,
  HStack,
  Integer,
  Show,
  Spinner,
  Text,
  VStack,
} from 'platform/foundation';
import {useFormatNumber} from 'platform/locale';
import {css, useTheme} from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {useEffect, useMemo, useRef, useState} from 'react';

import {head, isNil, isNotEmpty} from 'ramda';
import {isNotNil, round} from 'ramda-adjunct';

import {featureFlags} from '@dms/feature-flags';
import i18n from '@dms/i18n';

import {ApiError, EMPTY_PLACEHOLDER, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {SourcingType, useSourcingType} from '../../hooks/useSourcingType';
import {SourcingTableDispatch} from '../../store/sourcingTable/reducer';
import {getSourcingVehicleId} from '../../utils/getSourcingVehicleId';
import {isAuctionVehicle} from '../../utils/isAuctionVehicle';
import {AdvanceAuctionView} from '../AdvanceAuctionView/AdvanceAuctionView';
import {AdvanceVehicleView} from '../AdvanceVehicleView/AdvanceVehicleView';
import {DataViewBulkActionDrawer} from './components/DataViewBulkActionDrawer';
import {LiveLastUpdateTime} from './components/LiveLastUpdateTime';
import {SourcingTableRow} from './components/SourcingTableRow';
import {useHandleKeyboards} from './hooks/useHandleKeyboards';
import {useInfiniteScroll} from './hooks/useInfiniteScroll';
import {Row} from './types/Row';
import {SourcingTableState} from './types/SourcingTableState';
import {createRowElementId} from './utils/createRowElementId';
import {Column} from './utils/getSourcingTableColumns';
import {sourcingTableSessionStorage} from './utils/sourcingTableSessionStorage';

interface TableProps extends TestIdProps {
  state: SourcingTableState;
  dispatch: SourcingTableDispatch;
  columns: Column[];
  sortByOptions: ChoiceProps<string>['options'];
  data: Row[];
  isRealTimeEnabled?: boolean;
  isLoading: boolean;
  isFetching: boolean;
  error?: ApiError | Nullish;
  count?: number | Nullish;
  lastUpdateTime?: number | null;
  /** Sort id to force when real-time mode is toggled on.
   * Classifieds passes CLASSIFIEDS_DEFAULT_ORDER_ID; Auctions omits it to keep the current sort. */
  realTimeOrderBy?: string;
}

const HEIGHT_OF_TABLE_HEADER_DETAILED_VIEW = 12;
const HEIGHT_OF_TABLE_HEADER = 20;
const HEIGHT_OF_TABLE_HEADER_AND_FOOTER = 36;

export function SourcingTable(props: TableProps) {
  const formatNumber = useFormatNumber();
  const theme = useTheme();
  const sourcingType = useSourcingType();
  const [isDetailedView, setIsDetailedView] = useState(
    sourcingTableSessionStorage.getLastSelectedView()
  );
  const [scrollbarWidth, setScrollbarWidth] = useState(0);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // Calculate table scrollbar width to adjust padding of the header
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      const scrollbarWidth = scrollContainer.offsetWidth - scrollContainer.clientWidth;
      setScrollbarWidth(scrollbarWidth);
    }
  }, [props.data]);

  const onToggleDetailView = () => {
    if (!isDetailedView) {
      props.dispatch({type: 'changeCheckedRows', rows: []});
    }
    setIsDetailedView(!isDetailedView);
    sourcingTableSessionStorage.setLastSelectedView(!isDetailedView);
  };

  useHandleKeyboards(props.state.selectedRow, props.data, props.state.tableName, props.dispatch);

  const {page, selectedRow, tableName} = props.state;
  const {data, dispatch} = props;

  const hasMoreRows = props.data.length < (props.count ?? 0);
  const nextPage = (page ?? 1) + 1;
  useInfiniteScroll(scrollContainerRef, props.dispatch, props.isFetching, hasMoreRows, nextPage);

  // Set last visited page if data are still cached, otherwise set first page
  useEffect(() => {
    if (isNil(page) && isNotEmpty(data)) {
      const savedPage = sourcingTableSessionStorage.getLastVisitedPage(tableName);
      dispatch({type: 'changePage', page: savedPage ?? 1});
    }
  }, [data, dispatch, page, tableName]);

  // Select last visited vehicle and then scroll on it
  useEffect(() => {
    if (isNil(selectedRow) && isNotEmpty(data)) {
      const lastVisitedVehicleId = sourcingTableSessionStorage.getLastVisitedVehicleId(tableName);
      const lastVisitedVehicle = data.find(
        (item) => lastVisitedVehicleId === item.sourcingVehicle.adId
      );
      const rowToSelect = lastVisitedVehicle ?? head(data);
      if (isNotNil(rowToSelect)) {
        dispatch({type: 'selectRow', row: rowToSelect});
      }

      if (lastVisitedVehicle) {
        window.document
          .getElementById(createRowElementId(lastVisitedVehicle))
          ?.scrollIntoView({block: 'nearest'});
      }
    }
  }, [data, dispatch, selectedRow, tableName]);

  // Select first row if selected row is put to hidden list
  useEffect(() => {
    if (
      isNotNil(selectedRow) &&
      !data.some((row) => row.sourcingVehicle.adId === selectedRow.sourcingVehicle.adId)
    ) {
      const firstRow = head(data);
      if (isNotNil(firstRow)) {
        dispatch({type: 'selectRow', row: firstRow});
      }
    }
  }, [data, dispatch, selectedRow]);

  const handleRowCheckbox =
    (row: Row): NonNullable<CheckboxProps['onChange']> =>
    (value) => {
      if (value) {
        props.dispatch({type: 'changeCheckedRows', rows: [...props.state.checkedRows, row]});
      } else {
        props.dispatch({
          type: 'changeCheckedRows',
          rows: props.state.checkedRows.filter(
            (row1) => row1.sourcingVehicle?.adId !== row.sourcingVehicle?.adId
          ),
        });
      }
    };

  const onUncheckCheckedRows = (rowIds: string[]) => {
    props.dispatch({
      type: 'changeCheckedRows',
      rows: props.state.checkedRows.filter(
        (row1) => !rowIds.includes(getSourcingVehicleId(row1) ?? '')
      ),
    });
  };

  const onSetLastVisitedVehicle = (adId: string) => {
    sourcingTableSessionStorage.setLastVisitedVehicleId(props.state.tableName, adId);
  };

  const onSelectRow = (row: Row) => {
    props.dispatch({type: 'selectRow', row});
    const adId = getSourcingVehicleId(row);
    if (isNotNil(adId)) {
      onSetLastVisitedVehicle(adId);
    }
  };

  const isFooterVisible = isNotEmpty(props.state.checkedRows);
  const headerAndFooterHeight = match<[boolean, boolean], Integer>([
    isDetailedView,
    isFooterVisible,
  ])
    .with([true, Pattern.any], () => HEIGHT_OF_TABLE_HEADER_DETAILED_VIEW)
    .with([false, true], () => HEIGHT_OF_TABLE_HEADER_AND_FOOTER)
    .with([false, false], () => HEIGHT_OF_TABLE_HEADER)
    .exhaustive();

  const checkedRowIds = useMemo(
    () => new Set(props.state.checkedRows.map((row) => row.sourcingVehicle?.adId).filter(isNotNil)),
    [props.state.checkedRows]
  );

  const dataByAdId = useMemo(
    () =>
      new Map(
        props.data
          .filter((row) => isNotNil(row.sourcingVehicle?.adId))
          .map((row) => [row.sourcingVehicle.adId, row])
      ),
    [props.data]
  );

  const isCheckedAll =
    isNotEmpty(props.data) &&
    props.data.every((row) => checkedRowIds.has(row.sourcingVehicle?.adId));

  const checkedRows = props.state.checkedRows.map(
    (checkedRow) => dataByAdId.get(checkedRow.sourcingVehicle?.adId) ?? checkedRow
  );

  const isLoadingMore = props.isFetching && (props.state.page ?? 1) > 1;
  const isRealTimeEnabled =
    props.isRealTimeEnabled && isFeatureEnabled(featureFlags.SOURCING_REAL_TIME_ADVERTISEMENT);

  return (
    <Box
      backgroundColor="general.white"
      borderRadius="xSmall"
      boxShadow="elevation_1"
      height="100%"
      overflow="hidden"
    >
      <Box padding={2} paddingVertical={2}>
        <HStack
          align="center"
          justify={isRealTimeEnabled ? 'space-between' : 'flex-end'}
          spacing={2}
        >
          <Show when={isRealTimeEnabled}>
            <Box paddingLeft={2}>
              <Switch
                label={i18n.t('entity.sourcingTable.labels.realTime')}
                value={props.state.isRealTime}
                onChange={(value) =>
                  props.dispatch({
                    type: 'setRealTime',
                    isRealTime: value,
                    ...(props.realTimeOrderBy ? {orderBy: props.realTimeOrderBy} : {}),
                  })
                }
              />
            </Box>
          </Show>
          <HStack spacing={2}>
            <HStack align="center" spacing={2}>
              <Text size="xSmall" color="secondary">
                {i18n.t('general.actions.sortBy')}
              </Text>
              <Box width={40}>
                <Choice
                  value={props.state.orderBy}
                  onChange={(value) =>
                    props.dispatch({
                      type: 'sort',
                      column: value as string,
                      keepRealTime: !props.realTimeOrderBy,
                    })
                  }
                  options={props.sortByOptions ?? []}
                  isNotClearable
                  data-testid={suffixTestId('sortByOptions', props)}
                />
              </Box>
            </HStack>
            <Show when={isNotNil(props.count)}>
              <Separator orientation="vertical" spacing={0} />
              <HStack align="center">
                <Text size="xSmall" color="secondary">
                  {isNotNil(props.count)
                    ? i18n.t('general.labels.result', {
                        count: props.count,
                        formattedCount: formatNumber(props.count),
                      })
                    : EMPTY_PLACEHOLDER}
                </Text>
              </HStack>
            </Show>
            <LiveLastUpdateTime lastUpdateTime={props.lastUpdateTime} />
            <Separator orientation="vertical" spacing={0} />
            <IconButton
              icon={isDetailedView ? 'action/vertical_split' : 'action/view_stream'}
              onClick={onToggleDetailView}
              data-testid={suffixTestId('toggleDetailView', props)}
            />
          </HStack>
        </HStack>
      </Box>
      <Separator spacing={0} />
      <Hide when={isDetailedView}>
        <Box
          paddingLeft={3}
          paddingRight={(3 + round(scrollbarWidth * theme.space.step)) as Integer}
          paddingVertical={2}
        >
          <HStack spacing={2} align="center">
            <Box width={14}>
              <Checkbox
                value={isCheckedAll}
                data-testid={suffixTestId('selectAllCheckbox', props)}
                onChange={(value) =>
                  props.dispatch({type: 'changeCheckedRows', rows: value ? props.data : []})
                }
              />
            </Box>
            {props.columns.map((column) => (
              <Box
                key={column.id}
                width={column.width}
                minWidth={isNil(column.width) ? 65 : 0}
                flex={isNotNil(column.width) ? undefined : 1}
              >
                {column.Header}
              </Box>
            ))}
            <Box width={6} />
          </HStack>
        </Box>
        <Separator spacing={0} />
      </Hide>
      <DataStatus isError={isNotNil(props.error)} isLoading={props.isLoading} minHeight={50}>
        <Box height={`calc(100% - ${getSize(headerAndFooterHeight)})`} position="relative">
          <div
            css={css`
              height: 100%;
              overflow-y: scroll;
            `}
            ref={scrollContainerRef}
          >
            <VStack>
              {props.data.map((row, index) => (
                <div key={row.sourcingVehicle?.adId} id={row.sourcingVehicle?.adId}>
                  {!isDetailedView ? (
                    <>
                      <SourcingTableRow
                        key={row.sourcingVehicle?.adId}
                        onClick={() => onSelectRow(row)}
                        index={index}
                        columns={props.columns}
                        row={row}
                        isExpanded={props.state.expandedRowIds.includes(row.sourcingVehicle?.adId)}
                        onExpandedChange={(row, isExpanded) =>
                          props.dispatch({type: 'expandRow', row, isExpanded})
                        }
                        isSelected={
                          isNotNil(props.state.selectedRow) &&
                          props.state.selectedRow.sourcingVehicle?.adId ===
                            row.sourcingVehicle?.adId
                        }
                        isChecked={checkedRowIds.has(row.sourcingVehicle?.adId)}
                        onCheck={handleRowCheckbox(row)}
                        setLastVisitedVehicle={onSetLastVisitedVehicle}
                      />
                      <Show when={index < props.data?.length - 1}>
                        <Separator orientation="horizontal" spacing={0} />
                      </Show>
                    </>
                  ) : (
                    <>
                      {sourcingType === SourcingType.AUCTIONS && isAuctionVehicle(row) ? (
                        <AdvanceAuctionView
                          key={row.sourcingVehicle?.adId}
                          isLastClickedRow={
                            isNotNil(props.state.selectedRow) &&
                            props.state.selectedRow.sourcingVehicle?.adId ===
                              row.sourcingVehicle?.adId
                          }
                          onRowClick={() => onSelectRow(row)}
                          vehicle={{...row, shortcomings: [], highlights: []}}
                          setLastVisitedVehicle={onSetLastVisitedVehicle}
                          data-testid={suffixTestId('advanceVehicleView', props)}
                        />
                      ) : (
                        <AdvanceVehicleView
                          key={row.sourcingVehicle?.adId}
                          isLastClickedRow={
                            isNotNil(props.state.selectedRow) &&
                            props.state.selectedRow.sourcingVehicle?.adId ===
                              row.sourcingVehicle?.adId
                          }
                          onRowClick={() => onSelectRow(row)}
                          vehicle={{...row, shortcomings: [], highlights: []}}
                          setLastVisitedVehicle={onSetLastVisitedVehicle}
                          data-testid={suffixTestId('advanceVehicleView', props)}
                        />
                      )}
                      <Box height={2} backgroundColor="palettes.neutral.10.100" />
                    </>
                  )}
                </div>
              ))}
              <HStack height={16} align="center" justify="center">
                <Show when={isLoadingMore}>
                  <Spinner />
                </Show>
              </HStack>
            </VStack>
          </div>
        </Box>
        <Show when={isFooterVisible}>
          <Box
            padding={4}
            height={16}
            backgroundColor="general.white"
            borderTop="1px solid"
            borderColor="general.separator"
          >
            <VStack height="100%" justify="center">
              <Box height="100%">
                <DataViewBulkActionDrawer
                  selectedRows={checkedRows}
                  pathToVehicleAdId={['sourcingVehicle', 'adId']}
                  onUnselectSelectedRows={onUncheckCheckedRows}
                />
              </Box>
            </VStack>
          </Box>
        </Show>
      </DataStatus>
    </Box>
  );
}
